import React from "react";
import IPayment from "../../../models/IPayment";
import IPaymentSummary from "../../../models/IPaymentSummary";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { CreditCardBrandIcon } from "../../shared/CreditCardBrandIcon/CreditCardBrandIcon";
import HelperService from "../../../services/HelperService";
import UserService from "../../../services/UserService";
import "./EcommercePaymentSummary.scss";

type Props = {
    payments?: IPayment[];
    hidePricing: boolean;
    pricingPrecision?: number;
    /** Heading rendered above the payments, only when there is something to show. */
    title?: string;
};

// Card network names are brand names, so they are not translated.
const cardBrandName = (brand: string): string | null => {
    switch (brand.toLowerCase()) {
        case "visa":
            return "Visa";
        case "mastercard":
            return "Mastercard";
        case "amex":
            return "American Express";
        case "discover":
            return "Discover";
        case "diners":
            return "Diners Club";
        case "jcb":
            return "JCB";
        case "unionpay":
            return "UnionPay";
        default:
            return null;
    }
};

const PaymentMethod: React.FC<{ summary: IPaymentSummary }> = ({ summary }) => {
    const last4 = summary.last4 ? <span className={"payment-method-last4"}>•••• {summary.last4}</span> : null;

    switch (summary.method_type) {
        case "card":
        case "card_present": {
            const brandName = summary.brand ? cardBrandName(summary.brand) : null;

            return (
                <span className={"payment-method"}>
                    <span className={"payment-method-icon"}>
                        <CreditCardBrandIcon brand={summary.brand || ""} />
                    </span>
                    {brandName ? <span>{brandName}</span> : <OwLabel title={"card"} noBottomMargin={true} />}
                    {last4}
                </span>
            );
        }

        case "us_bank_account":
            return (
                <span className={"payment-method"}>
                    <span className={"payment-method-icon"}>
                        <i className={"fas fa-building-columns"} />
                    </span>
                    {summary.bank_name ? <span>{summary.bank_name}</span> : null}
                    {last4}
                </span>
            );

        case "link":
            return (
                <span className={"payment-method"}>
                    <span className={"payment-method-icon"}>
                        <i className={"fas fa-link"} />
                    </span>
                    <OwLabel title={"link"} noBottomMargin={true} />
                </span>
            );

        default:
            return null;
    }
};

/** The payments this component shows: summarized by the API, refund rows left out. */
export const getSummarizedPayments = (payments?: IPayment[]): IPayment[] =>
    (payments || []).filter((payment) => payment.payment_summary && !payment.is_refund);

/**
 * How the buyer paid, from the API's buyer-safe `payment_summary` (never the raw gateway payload). Renders nothing
 * when the order has no summarized payments.
 */
export const EcommercePaymentSummary: React.FC<Props> = (props) => {
    const payments = getSummarizedPayments(props.payments);

    if (!payments.length) {
        return null;
    }

    return (
        <div className={"EcommercePaymentSummary"}>
            {props.title ? <OwTitle title={props.title} tag={"h6"} isBold={true} /> : null}
            {payments.map((payment) => {
                const summary = payment.payment_summary!;

                return (
                    <div key={payment.guid} className={"payment-summary-row"}>
                        <div className={"d-flex flex-wrap align-items-center gap-2"}>
                            <PaymentMethod summary={summary} />
                            <span className={`payment-status is-${summary.status}`}>
                                <OwLabel title={summary.status} noBottomMargin={true} />
                            </span>
                        </div>
                        <div className={"payment-summary-meta"}>
                            {!props.hidePricing ? (
                                <span>
                                    {HelperService.formatCurrency({
                                        value: summary.amount,
                                        currency: "USD",
                                        precision: props.pricingPrecision || 2,
                                    })}
                                </span>
                            ) : null}
                            {summary.paid_at ? <span>{UserService.parseDate(summary.paid_at)}</span> : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
